import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useCrypto } from '@/infra/crypto'
import { useAuthStatus, useUserInitializationContext } from '@/features/user'
import type { RawActivityEventPayload } from '@/infra/sse/activity/decryption'
import { processRawActivityEvent } from '@/infra/sse/activity/decryption'
import type { RawConversationEventPayload } from '@/infra/sse/conversation/decryption'
import { processRawConversationEvent } from '@/infra/sse/conversation/decryption'
import {
  CLAUDE_CODE_CONVERSATION_EVENT,
  STATUS_SESSION_CHANGED_EVENT,
  STATUS_HOST_CHANGED_EVENT,
} from '@/infra/sse/constants'
import { useActivityEventsStream, useConversationEventsStream } from '@/index'
import type { SessionStatusChangedPayload, HostStatusChangedPayload } from '@/infra/sse/status'
import { useStatusEventsStream } from '@/infra/sse/status'

interface SSEConnectionState {
  readonly isConnected: boolean
  readonly isConnecting: boolean
  readonly conversationConnectionId: string | null
  readonly activityConnectionId: string | null
  readonly statusConnectionId: string | null
}

interface SSEConnectionContextValue extends SSEConnectionState {
  readonly disconnect: () => void
}

const SSEConnectionContext = createContext<SSEConnectionContextValue | null>(null)

interface SSEConnectionProviderProps {
  readonly children: ReactNode
}

export function SSEConnectionProvider({ children }: SSEConnectionProviderProps) {
  const { isAuthenticated, user } = useAuthStatus()
  const accessToken = user?.access_token
  const { userInitialized } = useUserInitializationContext()

  const { decrypt, isReady: cryptoReady } = useCrypto()

  const enabled = isAuthenticated && userInitialized && !!accessToken && cryptoReady

  const [conversationConnectionId, setConversationConnectionId] = useState<string | null>(null)
  const [activityConnectionId, setActivityConnectionId] = useState<string | null>(null)
  const [statusConnectionId, setStatusConnectionId] = useState<string | null>(null)

  const conversationDisconnectRef = useRef<(() => Promise<void>) | null>(null)
  const activityDisconnectRef = useRef<(() => Promise<void>) | null>(null)
  const statusDisconnectRef = useRef<(() => Promise<void>) | null>(null)

  const handleConversationConnected = useCallback((connectionId: string) => {
    setConversationConnectionId(connectionId)
    globalThis.dispatchEvent(
      new CustomEvent('sse-connection-id-received', {
        detail: { connectionId, streamType: 'conversation' },
      }),
    )
  }, [])

  const handleConversationDisconnected = useCallback(() => {
    setConversationConnectionId(null)
  }, [])

  const handleActivityConnected = useCallback((connectionId: string) => {
    setActivityConnectionId(connectionId)
  }, [])

  const handleActivityDisconnected = useCallback(() => {
    setActivityConnectionId(null)
  }, [])

  const handleStatusConnected = useCallback((connectionId: string) => {
    setStatusConnectionId(connectionId)
  }, [])

  const handleStatusDisconnected = useCallback(() => {
    setStatusConnectionId(null)
  }, [])

  const handleSessionStatusChange = useCallback((event: SessionStatusChangedPayload) => {
    globalThis.dispatchEvent(new CustomEvent(STATUS_SESSION_CHANGED_EVENT, { detail: event }))
  }, [])

  const handleHostStatusChange = useCallback((event: HostStatusChangedPayload) => {
    globalThis.dispatchEvent(new CustomEvent(STATUS_HOST_CHANGED_EVENT, { detail: event }))
  }, [])

  const handleActivityEvent = useCallback(
    (event: RawActivityEventPayload) => {
      // Fire-and-forget: Activity events are independent point-in-time snapshots.
      // Sub-ms AES-GCM decryption variance cannot produce meaningful reordering.
      // Timestamps in eventMetadata provide ordering if consumers need it.
      void (async () => {
        try {
          const processed = await processRawActivityEvent(event, cryptoReady ? decrypt : null)
          globalThis.dispatchEvent(new CustomEvent('activity-event', { detail: processed }))
        } catch (error) {
          // eslint-disable-next-line no-console -- Intentional: decryption failure diagnostics
          console.warn(
            '[SSE] Activity event decryption failed, falling back to plain extraction:',
            error,
          )
          try {
            const fallback = await processRawActivityEvent(event, null)
            globalThis.dispatchEvent(new CustomEvent('activity-event', { detail: fallback }))
          } catch (fallbackError) {
            // eslint-disable-next-line no-console -- Intentional: complete processing failure diagnostics
            console.error('[SSE] Activity event processing failed entirely:', {
              decryptionError: error,
              fallbackError,
              eventId: event.eventMetadata.eventId,
            })
          }
        }
      })()
    },
    [decrypt, cryptoReady],
  )

  const handleConversationEvent = useCallback(
    (event: RawConversationEventPayload) => {
      void (async () => {
        try {
          const processed = await processRawConversationEvent(event, cryptoReady ? decrypt : null)
          globalThis.dispatchEvent(
            new CustomEvent(CLAUDE_CODE_CONVERSATION_EVENT, { detail: processed }),
          )
        } catch (error) {
          // eslint-disable-next-line no-console -- Intentional: decryption failure diagnostics
          console.warn(
            '[SSE] Conversation event processing failed, falling back to plain extraction:',
            error,
          )
          try {
            const fallback = await processRawConversationEvent(event, null)
            globalThis.dispatchEvent(
              new CustomEvent(CLAUDE_CODE_CONVERSATION_EVENT, { detail: fallback }),
            )
          } catch (fallbackError) {
            // eslint-disable-next-line no-console -- Intentional: complete processing failure diagnostics
            console.error('[SSE] Conversation event processing failed entirely:', {
              decryptionError: error,
              fallbackError,
              eventId: event.eventMetadata.eventId,
            })
          }
        }
      })()
    },
    [decrypt, cryptoReady],
  )

  const conversationStream = useConversationEventsStream({
    accessToken,
    enabled,
    onEvent: handleConversationEvent,
    onConnected: handleConversationConnected,
    onDisconnected: handleConversationDisconnected,
  })

  const activityStream = useActivityEventsStream({
    accessToken,
    enabled,
    onEvent: handleActivityEvent,
    onConnected: handleActivityConnected,
    onDisconnected: handleActivityDisconnected,
  })

  const statusStream = useStatusEventsStream({
    accessToken,
    enabled,
    onSessionStatusChange: handleSessionStatusChange,
    onHostStatusChange: handleHostStatusChange,
    onConnected: handleStatusConnected,
    onDisconnected: handleStatusDisconnected,
  })

  useEffect(() => {
    conversationDisconnectRef.current = conversationStream.disconnect
    activityDisconnectRef.current = activityStream.disconnect
    statusDisconnectRef.current = statusStream.disconnect
  }, [conversationStream.disconnect, activityStream.disconnect, statusStream.disconnect])

  const disconnect = useCallback(() => {
    conversationDisconnectRef.current?.()
    activityDisconnectRef.current?.()
    statusDisconnectRef.current?.()
    globalThis.dispatchEvent(new CustomEvent('sse-disconnected'))
  }, [])

  const isFullyConnected = useMemo(
    () => conversationStream.isConnected && activityStream.isConnected && statusStream.isConnected,
    [conversationStream.isConnected, activityStream.isConnected, statusStream.isConnected],
  )

  const isConnecting = useMemo(
    () =>
      conversationStream.isConnecting || activityStream.isConnecting || statusStream.isConnecting,
    [conversationStream.isConnecting, activityStream.isConnecting, statusStream.isConnecting],
  )

  useEffect(() => {
    if (
      isFullyConnected &&
      conversationConnectionId &&
      activityConnectionId &&
      statusConnectionId
    ) {
      globalThis.dispatchEvent(
        new CustomEvent('sse-fully-connected', {
          detail: { conversationConnectionId, activityConnectionId, statusConnectionId },
        }),
      )
    }
  }, [isFullyConnected, conversationConnectionId, activityConnectionId, statusConnectionId])

  const contextValue = useMemo<SSEConnectionContextValue>(
    () => ({
      isConnected: isFullyConnected,
      isConnecting,
      conversationConnectionId,
      activityConnectionId,
      statusConnectionId,
      disconnect,
    }),
    [
      isFullyConnected,
      isConnecting,
      conversationConnectionId,
      activityConnectionId,
      statusConnectionId,
      disconnect,
    ],
  )

  return (
    <SSEConnectionContext.Provider value={contextValue}>{children}</SSEConnectionContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSSEConnectionContext(): SSEConnectionContextValue {
  const context = useContext(SSEConnectionContext)
  if (!context) {
    throw new Error('useSSEConnectionContext must be used within SSEConnectionProvider')
  }
  return context
}
